import React, { useCallback, useRef, useState } from "react";
import { ImagePlus, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

export default function ImageDropzone({ value, onChange, label = "Post image", testidPrefix = "image-dropzone" }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        toast.error("Please choose an image file");
        return;
      }
      if (file.size > 6 * 1024 * 1024) {
        toast.error("Image too large (max 6 MB)");
        return;
      }
      try {
        const dataUrl = await fileToDataUrl(file);
        onChange?.(dataUrl);
        toast.success("Image attached");
      } catch (_e) {
        toast.error("Could not read image");
      }
    },
    [onChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(e.dataTransfer.files);
  };

  if (value) {
    return (
      <div className="relative rounded-[14px] overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]" data-testid={`${testidPrefix}-filled`}>
        <img src={value} alt="selected" className="w-full aspect-square object-cover" />
        <div className="absolute top-2 right-2 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="border-[color:var(--border-subtle)] bg-[color:var(--bg-1)]/80 text-[color:var(--text-1)] backdrop-blur"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testidPrefix}-replace`}
          >
            Replace
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-[color:rgba(255,92,108,0.45)] bg-[color:var(--bg-1)]/80 text-[color:var(--danger)] backdrop-blur"
            onClick={() => onChange?.(null)}
            data-testid={`${testidPrefix}-clear`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      data-testid={testidPrefix}
      className={
        "cursor-pointer rounded-[14px] border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition-colors " +
        (drag
          ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.06)]"
          : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] hover:border-[color:var(--border-strong)]")
      }
    >
      <div className="h-10 w-10 rounded-md bg-[color:rgba(27,168,159,0.12)] flex items-center justify-center text-[color:var(--brand-primary)] mb-3">
        <ImagePlus className="h-4 w-4" />
      </div>
      <div className="font-display text-sm font-semibold text-[color:var(--text-1)]">Drop an image, or click to upload</div>
      <div className="text-xs text-[color:var(--text-3)] mt-1">{label} · PNG, JPG up to 6 MB</div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-3 border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] text-[color:var(--text-1)]"
        data-testid={`${testidPrefix}-browse`}
      >
        <Upload className="h-3.5 w-3.5 mr-1" /> Choose file
      </Button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
    </div>
  );
}
